import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatIconModule} from "@angular/material/icon";
import {GruppeMedlemListeModell, TilgangsrolleMedlem} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {MatTooltipModule} from "@angular/material/tooltip";
import {MatButtonModule} from "@angular/material/button";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {TranslateModule} from "@ngx-translate/core";

const debug = initDebugger('MøteOpprettPubliseringKontroller');

type SkjemaType = ReturnType<typeof MøteOpprettPubliseringKontroller.opprettSkjema>;
type SkjemaVerdier = SkjemaType['value'];

@Component({
    selector: 'moete-opprett-publisering',
    templateUrl: './møte-opprett-publisering.html',
    styleUrl: './møte-opprett-publisering.scss',
    imports: [
        ReactiveFormsModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatIconModule,
        MatTooltipModule,
        MatButtonModule,
        MatSlideToggleModule,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MøteOpprettPubliseringKontroller {

    private _antallDeltakere = 0;
    private _skjema!: SkjemaType;

    static opprettSkjema() {
        return new FormGroup({
            erMoetePublisert: new FormControl(true, {nonNullable: true}),
            erProtokollPublisert: new FormControl(true, {nonNullable: true}),
            sendInnkalling: new FormControl(false, {nonNullable: true})
        })
    }

    /** steng med navn på medlemmer som har tilgang til møtet selvom det ikke er publisert.
     * Settes via {@link #gruppemedlemmer} */
    medlemmerMedTilgangTilIkkePublisert = '';

    constructor(private oversettelser: Oversettelser) {
    }

    @Input({required: true}) set antallDeltakere(antall: number) {
        this._antallDeltakere = antall;
        this.setSendInnkallingDisabledDersomIngenDeltakere();
    }

    get antallDeltakere() {
        return this._antallDeltakere;
    }

    @Input({required: true}) set skjema(skjema: SkjemaType) {
        this._skjema = skjema;
        this.setSendInnkallingDisabledDersomIngenDeltakere();
    }

    get skjema() {
        return this._skjema;
    }

    @Input({required: true}) set gruppemedlemmer(medlemmer: GruppeMedlemListeModell[] | null) {
        this.medlemmerMedTilgangTilIkkePublisert = '';
        if (erNil(medlemmer)) return;
        this.medlemmerMedTilgangTilIkkePublisert = medlemmer
          .filter(medlem => medlem.tilgangsroller.some(rolle => rolle == TilgangsrolleMedlem.tilgangIkkePublisert || rolle == TilgangsrolleMedlem.skrive))
          .map(medlem => medlem.navn)
          .join(', ')
    }

    sendInnkallingTooltip() {
        return this.antallDeltakere == 0 ?
          this.oversettelser.oversett(i18n('Du må ha valgt møtedeltakere for å kunne sende innkalling')) :
          this.oversettelser.oversett(i18n('Huker du av her, får du etter opprettelse av møtet velge hvem du vil sende innkalling til'));
    }

    // private hjelpemetoder

    private setSendInnkallingDisabledDersomIngenDeltakere() {
        if (erNil(this.skjema)) return;

        if (this.antallDeltakere == 0) this.skjema.controls.sendInnkalling.disable();
        else this.skjema.controls.sendInnkalling.enable();
    }
}
